import { useState } from "react";

interface Player {
  id: number;
  name: string;
  role: string;
  rating: number;
}

interface MapStat {
  map: string;
  winRate: number; // 0–100
  sampleSize: number;
}

interface RecentMatch {
  id: number;
  opponent: string;
  result: "W" | "L";
  score: string;
  map: string;
  date: string;
  demoLink?: string;
}

interface TeamProfileData {
  id: number;
  name: string;
  division: string;
  elo: number;
  recentForm: string; // e.g. "WWLWW"
  streak: string; // e.g. "3W"
  riskLevel: "Low" | "Medium" | "High";
  players: Player[];
  mapPool: MapStat[];
  recentMatches: RecentMatch[];
  tendencies: string[];
}

const MOCK_TEAMS: TeamProfileData[] = [
  {
    id: 1,
    name: "Team Alpha",
    division: "Advanced",
    elo: 2380,
    recentForm: "WWLWW",
    streak: "3W",
    riskLevel: "High",
    players: [
      { id: 1, name: "Alpha1", role: "AWPer", rating: 1.18 },
      { id: 2, name: "Alpha2", role: "IGL", rating: 1.02 },
      { id: 3, name: "Alpha3", role: "Entry", rating: 1.05 },
      { id: 4, name: "Alpha4", role: "Anchor", rating: 0.98 },
      { id: 5, name: "Alpha5", role: "Flex", rating: 1.01 },
    ],
    mapPool: [
      { map: "Mirage", winRate: 62, sampleSize: 21 },
      { map: "Inferno", winRate: 55, sampleSize: 18 },
      { map: "Nuke", winRate: 48, sampleSize: 15 },
      { map: "Overpass", winRate: 70, sampleSize: 10 },
    ],
    recentMatches: [
      {
        id: 1,
        opponent: "Team Beta",
        result: "W",
        score: "16-10",
        map: "Mirage",
        date: "2026-01-18",
        demoLink: "#",
      },
      {
        id: 2,
        opponent: "Team Gamma",
        result: "L",
        score: "13-16",
        map: "Inferno",
        date: "2026-01-15",
      },
      {
        id: 3,
        opponent: "Team Delta",
        result: "W",
        score: "16-8",
        map: "Overpass",
        date: "2026-01-10",
        demoLink: "#",
      },
    ],
    tendencies: [
      "Aggressive CT setups on Mirage mid.",
      "Frequent B execs on Inferno with late lurks.",
      "AWPer likes to re-peek after first contact.",
    ],
  },
  {
    id: 2,
    name: "Team Beta",
    division: "Main",
    elo: 2250,
    recentForm: "WLWLW",
    streak: "1W",
    riskLevel: "Medium",
    players: [
      { id: 1, name: "Beta1", role: "AWPer", rating: 1.10 },
      { id: 2, name: "Beta2", role: "IGL", rating: 0.99 },
      { id: 3, name: "Beta3", role: "Entry", rating: 1.03 },
      { id: 4, name: "Beta4", role: "Anchor", rating: 0.95 },
      { id: 5, name: "Beta5", role: "Flex", rating: 1.00 },
    ],
    mapPool: [
      { map: "Inferno", winRate: 68, sampleSize: 20 },
      { map: "Ancient", winRate: 52, sampleSize: 12 },
      { map: "Vertigo", winRate: 40, sampleSize: 10 },
    ],
    recentMatches: [
      {
        id: 4,
        opponent: "Team Alpha",
        result: "L",
        score: "10-16",
        map: "Mirage",
        date: "2026-01-18",
      },
      {
        id: 5,
        opponent: "Team Echo",
        result: "W",
        score: "16-12",
        map: "Inferno",
        date: "2026-01-14",
      },
    ],
    tendencies: [
      "Slow T defaults with late site hits.",
      "CT sides rely heavily on utility to delay.",
    ],
  },
];

type TeamTab = "overview" | "roster" | "mapPool" | "recentMatches" | "tendencies";

export function TeamProfile() {
  const [teams] = useState<TeamProfileData[]>(MOCK_TEAMS);
  const [activeTeamId, setActiveTeamId] = useState<number>(
  MOCK_TEAMS[0]?.id ?? 0 );
  const [activeTab, setActiveTab] = useState<TeamTab>("overview");

  const activeTeam = teams.find((t) => t.id === activeTeamId);

  if (!activeTeam) {
    return (
      <div className="p-6">
        <p className="text-muted">No team selected.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full gap-6">
      {/* LEFT SIDEBAR: Opponent List */}
      <aside className="w-64 bg-surface border border-border rounded-lg p-4 flex flex-col">
        <div className="mb-4">
          <h2 className="text-sm font-semibold mb-2">Tracked Teams</h2>
          <input
            type="text"
            placeholder="Search teams..."
            className="
              w-full bg-bg border border-border rounded-md px-3 py-2
              text-sm focus:outline-none focus:border-[#4FC3F7]
            "
          />
        </div>

        <div className="space-y-1 overflow-y-auto">
          {teams.map((team) => (
            <button
              key={team.id}
              onClick={() => setActiveTeamId(team.id)}
              className={`
                w-full text-left px-3 py-2 rounded-md text-sm
                flex flex-col gap-0.5
                ${
                  team.id === activeTeamId
                    ? "bg-[#4FC3F7]/10 border border-[#4FC3F7] text-white"
                    : "hover:bg-bg border border-transparent text-muted hover:text-white"
                }
              `}
            >
              <span className="font-medium">{team.name}</span>
              <span className="text-xs text-muted">
                {team.division} • ELO {team.elo}
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* MAIN CONTENT + RIGHT SIDEBAR WRAPPER */}
      <div className="flex-1 flex gap-6">
        {/* MAIN PANEL */}
        <main className="flex-1 bg-surface border border-border rounded-lg p-6 flex flex-col">
          {/* Top: Team header + tabs */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Team header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold">{activeTeam.name}</h1>
                <p className="text-sm text-muted">
                  {activeTeam.division} • ELO {activeTeam.elo}
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <p className="text-muted text-xs">Recent Form</p>
                  <p className="font-semibold">{activeTeam.recentForm}</p>
                </div>
                <div>
                  <p className="text-muted text-xs">Streak</p>
                  <p className="font-semibold">{activeTeam.streak}</p>
                </div>
                <div>
                  <p className="text-muted text-xs">Risk Level</p>
                  <p
                    className={
                      activeTeam.riskLevel === "High"
                        ? "text-red-400 font-semibold"
                        : activeTeam.riskLevel === "Medium"
                        ? "text-yellow-400 font-semibold"
                        : "text-green-400 font-semibold"
                    }
                  >
                    {activeTeam.riskLevel}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border text-sm">
              {[
                { id: "overview", label: "Overview" },
                { id: "roster", label: "Roster" },
                { id: "mapPool", label: "Map Pool" },
                { id: "recentMatches", label: "Recent Matches" },
                { id: "tendencies", label: "Tendencies" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TeamTab)}
                  className={`
                    px-3 py-2 -mb-px border-b-2
                    ${
                      activeTab === tab.id
                        ? "border-[#4FC3F7] text-white"
                        : "border-transparent text-muted hover:text-white"
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto space-y-6">
            {activeTab === "overview" && (
              <OverviewSection team={activeTeam} />
            )}

            {activeTab === "roster" && (
              <RosterSection players={activeTeam.players} />
            )}

            {activeTab === "mapPool" && (
              <MapPoolSection maps={activeTeam.mapPool} />
            )}

            {activeTab === "recentMatches" && (
              <RecentMatchesSection matches={activeTeam.recentMatches} />
            )}

            {activeTab === "tendencies" && (
              <TendenciesSection tendencies={activeTeam.tendencies} />
            )}
          </div>
        </main>

        {/* RIGHT SIDEBAR: Quick Actions */}
        <aside className="w-72 bg-surface border border-border rounded-lg p-4 flex flex-col gap-4">
          <div>
            <h2 className="text-sm font-semibold mb-2">Quick Actions</h2>
            <div className="flex flex-col gap-2 text-sm">
              <button
                className="
                  w-full px-3 py-2 rounded-md font-medium
                  bg-[#4FC3F7] text-[#0C0F14]
                  hover:bg-[#81D4FA] transition
                "
              >
                Prep for Next Match
              </button>
              <button
                className="
                  w-full px-3 py-2 rounded-md font-medium border border-border
                  hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition
                "
              >
                Open Opponent Scouting
              </button>
              <button
                className="
                  w-full px-3 py-2 rounded-md font-medium border border-border
                  hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition
                "
              >
                Add Note About This Team
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-semibold mb-2">Scouting Summary</h2>
            <p className="text-xs text-muted">
              Use this profile as a hub for opponent analysis, map prep, and
              match planning. Connect this team to upcoming matches and match
              prep notes for a full workflow.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ---------- Sections ---------- */

function OverviewSection({ team }: { team: TeamProfileData }) {
  return (
    <section className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-bg border border-border rounded-lg p-4">
          <p className="text-xs text-muted mb-1">Division</p>
          <p className="text-lg font-semibold">{team.division}</p>
        </div>
        <div className="bg-bg border border-border rounded-lg p-4">
          <p className="text-xs text-muted mb-1">ELO</p>
          <p className="text-lg font-semibold">{team.elo}</p>
        </div>
        <div className="bg-bg border border-border rounded-lg p-4">
          <p className="text-xs text-muted mb-1">Recent Form</p>
          <p className="text-lg font-semibold">{team.recentForm}</p>
        </div>
      </div>

      <div className="bg-bg border border-border rounded-lg p-4">
        <p className="text-sm font-semibold mb-2">High‑Level Read</p>
        <p className="text-sm text-muted">
          Use this section to summarize how this team plays overall—tempo,
          comfort maps, star players, and general style. Later, this can be
          auto‑generated from stats and tendencies.
        </p>
      </div>
    </section>
  );
}

function RosterSection({ players }: { players: Player[] }) {
  return (
    <section className="space-y-4">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {players.map((p) => (
          <div
            key={p.id}
            className="bg-bg border border-border rounded-lg p-4 flex flex-col gap-1"
          >
            <p className="font-semibold">{p.name}</p>
            <p className="text-xs text-muted">{p.role}</p>
            <p className="text-xs text-muted">
              Rating: <span className="font-semibold">{p.rating.toFixed(2)}</span>
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function MapPoolSection({ maps }: { maps: MapStat[] }) {
  return (
    <section className="space-y-4">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {maps.map((m) => (
          <div
            key={m.map}
            className="bg-bg border border-border rounded-lg p-4 flex flex-col gap-2"
          >
            <div className="flex justify-between items-center">
              <p className="font-semibold">{m.map}</p>
              <p className="text-sm text-[#4FC3F7]">{m.winRate}%</p>
            </div>
            <p className="text-xs text-muted">
              {m.sampleSize} maps played recently
            </p>
            <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-[#4FC3F7]"
                style={{ width: `${m.winRate}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecentMatchesSection({ matches }: { matches: RecentMatch[] }) {
  return (
    <section className="space-y-4">
      <div className="space-y-2">
        {matches.map((m) => (
          <div
            key={m.id}
            className="bg-bg border border-border rounded-lg p-4 flex justify-between items-center text-sm"
          >
            <div>
              <p className="font-semibold">
                vs {m.opponent} • {m.map}
              </p>
              <p className="text-xs text-muted">{m.date}</p>
            </div>
            <div className="flex items-center gap-4">
              <p
                className={
                  m.result === "W"
                    ? "text-green-400 font-semibold"
                    : "text-red-400 font-semibold"
                }
              >
                {m.result} {m.score}
              </p>
              {m.demoLink && (
                <a
                  href={m.demoLink}
                  className="text-xs text-muted hover:text-[#4FC3F7] transition"
                >
                  View Demo
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TendenciesSection({ tendencies }: { tendencies: string[] }) {
  return (
    <section className="space-y-4">
      <div className="space-y-2">
        {tendencies.map((t, idx) => (
          <div
            key={idx}
            className="bg-bg border border-border rounded-lg p-4 text-sm"
          >
            {t}
          </div>
        ))}
        {tendencies.length === 0 && (
          <p className="text-sm text-muted">
            No tendencies recorded yet. Use this section to track how this team
            plays and how you want to punish them.
          </p>
        )}
      </div>
    </section>
  );
}